import {formatSlotDateTime as formatLiveSlotDateTime, browserTimeZone} from '@/intake/formatSlotDateTime'
/**
 * Matchmaking v2 client for /fa/intake match steps.
 * Prefer authenticated /api/v2 when VUE_APP_MATCHMAKING_V2_API=true (Bearer via axios).
 * Fall back to local unauth sandbox when only VUE_APP_MATCHMAKING_V2_SANDBOX=true.
 */
import store from '@/store'
import {isRateLimitError, rateLimitMessage, rememberRateLimit, currentRateLimitError, requestLanguage} from '@/intake/requestRateLimit'
import {buildScreeningAnswerLookup} from '@/intake/draftToScreeningAnswers'
import {
  currentWriteRevision,
  loadScreeningStore,
  saveScreeningStore,
} from '@/intake/screeningSession'

function truthyEnv(value) {
  if (value === true || value === 1) {
    return true
  }
  const s = String(value == null ? '' : value).toLowerCase()
  return s === 'true' || s === '1' || s === 'yes'
}

export function isMatchmakingV2ApiEnabled() {
  return truthyEnv(process.env.VUE_APP_MATCHMAKING_V2_API)
}

export function isSandboxMatchEnabled() {
  return isMatchmakingV2ApiEnabled() || truthyEnv(process.env.VUE_APP_MATCHMAKING_V2_SANDBOX)
}

function requestType() {
  if (isMatchmakingV2ApiEnabled()) {
    return 'matchmaking_v2'
  }
  return 'matchmaking_v2_sandbox'
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function sandboxErrorFromAxios(er) {
  const body = er && er.response && er.response.data
  if (isRateLimitError(er)) {
    const metadata = rememberRateLimit(er)
    const err = new Error(rateLimitMessage(er, requestLanguage()))
    err.status = 429
    err.code = 'RATE_LIMITED'
    err.retryable = true
    err.retryAt = metadata.retryAt
    err.retryAfterSeconds = metadata.retryAfterSeconds
    err.response = er.response
    err.headers = er.headers || (er.response && er.response.headers)
    err.data = (body && body.data) || er.data || {}
    err.sendJson = body || er.sendJson
    return err
  }
  if (body && (body.type === 'FAILURE' || body.data || body.message)) {
    const code = (body.data && body.data.code) || null
    const err = new Error(body.message || code || 'FAILURE')
    err.status = er.response.status
    err.data = body.data || {}
    err.code = code
    err.sendJson = body
    err.response = er.response
    err.headers = er.headers || er.response.headers
    return err
  }
  return er
}

function unwrapSuccess(resp) {
  if (resp && resp.type === 'SUCCESS') {
    return resp.data || {}
  }
  const err = new Error((resp && resp.message) || 'FAILURE')
  err.status = resp && resp.status_code
  err.data = (resp && resp.data) || {}
  err.code = err.data.code || null
  err.sendJson = resp
  throw err
}

function matchRequest(name, paramsQuery, body, requestOptions = {}) {
  const limited = currentRateLimitError()
  if (limited) return Promise.reject(limited)
  const headers = {
    'Idempotency-Key': requestOptions.idempotencyKey || idempotencyKey(name),
    'Trace-Id': requestOptions.traceId || ('tr_intake_' + uuid().replace(/-/g, '').slice(0, 12)),
  }
  return store.dispatch('generalRequest', {
    type: requestType(),
    name,
    paramsQuery: paramsQuery || {},
    body: body || {},
    headers,
  }).then(unwrapSuccess).catch((er) => {
    throw sandboxErrorFromAxios(er)
  })
}

function idempotencyKey(prefix) {
  const raw = String(prefix || 'req') + '-' + uuid()
  return raw.length >= 16 ? raw.slice(0, 200) : (raw + 'xxxxxxxxxxxxxxxx').slice(0, 32)
}

function stableScreeningKey(operation, scope) {
  const store = loadScreeningStore()
  const keys = Object.assign({}, store.idempotencyKeys || {})
  const normalizedScope = String(scope || 'root').replace(/[^A-Za-z0-9_.:-]/g, '_')
  const keyName = operation + ':' + normalizedScope
  if (!keys[keyName]) {
    keys[keyName] = ('screening-' + operation + '-' + normalizedScope + '-' + uuid()).slice(0, 200)
    saveScreeningStore({idempotencyKeys: keys})
  }
  return keys[keyName]
}

/**
 * Fingerprint answer payloads so retries of the *same* body reuse the Idempotency-Key,
 * while a changed age/answer at the same client_seq gets a fresh key (avoids
 * IDEMPOTENCY_CONFLICT when the user edits and resubmits).
 */
function screeningWriteFingerprint(body) {
  const payload = body || {}
  const normalized = {
    question_id: payload.question_id == null ? null : String(payload.question_id),
    answer_ids: Array.isArray(payload.answer_ids)
      ? payload.answer_ids.map(String)
      : [],
    ages: payload.ages == null ? null : payload.ages,
    participant_ref: payload.participant_ref == null ? null : String(payload.participant_ref),
    ...(payload.respondent_confirmation == null ? {} : {respondent_confirmation:String(payload.respondent_confirmation)}),
    answers: Array.isArray(payload.answers) ? payload.answers : null,
    client_seq: payload.client_seq == null ? null : Number(payload.client_seq),
    client_revision: payload.client_revision == null ? null : Number(payload.client_revision),
  }
  const raw = JSON.stringify(normalized)
  let hash = 2166136261
  for (let i = 0; i < raw.length; i += 1) {
    hash ^= raw.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16)
}

function answerIdempotencyKey(operationPrefix, intakeId, body) {
  const payload = body || {}
  if (payload.client_seq == null) {
    return idempotencyKey(operationPrefix)
  }
  const fingerprint = screeningWriteFingerprint(payload)
  return stableScreeningKey(
    operationPrefix + '-' + String(payload.client_seq) + '-' + fingerprint,
    intakeId,
  )
}

export function createSnapshotFromDraft(draft) {
  return matchRequest('fromDraft', {}, {draft})
}

export function startScreeningSession(marketId, options = {}) {
  const market = marketId || 'IR'
  if (options && options.forceNew) {
    const store = loadScreeningStore()
    const keys = Object.assign({}, store.idempotencyKeys || {})
    delete keys['start:' + market]
    saveScreeningStore({idempotencyKeys: keys})
  }
  return matchRequest('startScreeningSession', {}, {
    market_id: market,
  }, {
    idempotencyKey: stableScreeningKey('start', market),
  })
}

export function getScreeningSession(intakeId) {
  return matchRequest('getScreeningSession', {intakeId}, {})
}

export function reviseScreeningPreferences(intakeId) {
  return matchRequest('reviseScreeningPreferences', {intakeId}, {}, {
    idempotencyKey: stableScreeningKey('revise-preferences', intakeId),
  })
}

export function answerScreening(intakeId, payload) {
  const body = payload || {}
  return matchRequest('answerScreening', {intakeId}, body, {
    idempotencyKey: answerIdempotencyKey('answer', intakeId, body),
  })
}

export function answerScreeningBatch(intakeId, payload) {
  const body = payload || {}
  return matchRequest('answerScreeningBatch', {intakeId}, body, {
    idempotencyKey: answerIdempotencyKey('answer-batch', intakeId, body),
  })
}

export function finalizeScreening(intakeId) {
  return matchRequest('finalizeScreening', {intakeId}, {}, {
    idempotencyKey: stableScreeningKey('finalize', intakeId),
  })
}

export function prevScreening(intakeId) {
  return matchRequest('prevScreening', {intakeId}, {
    client_revision: currentWriteRevision(),
  }, {
    idempotencyKey: stableScreeningKey(
      'prev-' + String(currentWriteRevision()),
      intakeId
    ),
  })
}

/**
 * Authenticated API path: start screening → answer from draft map → finalize.
 * Does not fall back to from-draft.
 */
export async function createSnapshotViaScreening(draft) {
  const lookup = buildScreeningAnswerLookup(draft)

  let session = await startScreeningSession('IR')
  const intakeId = session.intake_id
  if (!intakeId) {
    throw new Error('INTAKE_ID_MISSING')
  }

  let clientSeq = Number(session.client_seq || 0) + 1
  let clientRevision = Number(session.write_revision || 0)
  let guard = 0
  while (guard < 80) {
    guard += 1
    if (session.finalize_ready || session.terminal_status) {
      break
    }
    const next = session.next_question
    const qid = next && next.question_id
    if (!qid) {
      break
    }
    const mapped = lookup[qid]
    if (!mapped) {
      const err = new Error('سوال بدون نگاشت: ' + qid)
      err.code = 'UNMAPPED_QUESTION'
      err.questionId = qid
      throw err
    }
    const body = {
      question_id: qid,
      answer_ids: mapped.answer_ids || [],
      client_seq: clientSeq,
      client_revision: clientRevision,
    }
    if (mapped.ages) {
      body.ages = mapped.ages
    }
    if (mapped.participant_ref) {
      body.participant_ref = mapped.participant_ref
    }
    session = await answerScreening(intakeId, body)
    clientSeq = Number(session.client_seq || clientSeq) + 1
    clientRevision = Number(session.write_revision || clientRevision)
  }

  if (!session.finalize_ready && !session.terminal_status) {
    const err = new Error('غربالگری برای نهایی‌سازی آماده نیست')
    err.code = 'INTAKE_NOT_READY'
    err.session = session
    throw err
  }

  const finalized = await finalizeScreening(intakeId)
  return Object.assign({}, finalized, {
    screening_intake_id: intakeId,
  })
}

export function createMatchRun(snapshotId, requestOptions) {
  return matchRequest('createMatchRun', {}, {
    client_requirement_snapshot_id: snapshotId,
    ...(isMatchmakingV2ApiEnabled() ? {display_timezone: browserTimeZone()} : {}),
  }, requestOptions)
}

export function getRecommendation(recommendationId) {
  return matchRequest('getRecommendation', {recommendationId}, {})
}

export function selectMember(runId, payload, requestOptions) {
  return matchRequest('select', {runId}, payload, requestOptions)
}

export function getMemberSlots(recommendationId, memberId) {
  return matchRequest('memberSlots', {recommendationId, memberId}, {})
}

/**
 * Map live memberSlots / price_quote payload for intake slot UI.
 * Never invents availability or client-declared prices.
 *
 * @param {object|null|undefined} data
 * @returns {{
 *   slots: Array<{slot_id: string, starts_at: string, label: string, ends_at: string|null}>,
 *   priceQuoteId: string|null,
 *   priceAmount: number|null,
 *   currency: string|null,
 * }}
 */
export function mapLiveMemberSlots(data, lang = 'fa') {
  const slots = Array.isArray(data && data.slots) ? data.slots : []
  const quote = (data && data.price_quote) || {}
  return {
    slots: slots
      .filter((s) => s && s.slot_id != null && s.starts_at)
      .map((s) => {
        const startsAt = String(s.starts_at)
        const formatted = formatLiveSlotDateTime(startsAt, lang)
        return {
          slot_id: String(s.slot_id),
          starts_at: startsAt,
          label: formatted.combinedLabel || startsAt,
          dateLabel: formatted.dateLabel,
          timeLabel: formatted.timeLabel,
          ends_at: s.ends_at != null ? String(s.ends_at) : null,
        }
      }),
    priceQuoteId: quote.quote_id ? String(quote.quote_id) : null,
    priceAmount: quote.amount != null && quote.amount !== ''
      ? Number(quote.amount)
      : null,
    currency: quote.currency ? String(quote.currency) : null,
  }
}

/**
 * True when validate-match failed for stale/invalid slot or quote (stay on slots).
 * @param {string[]|string|null|undefined} reasonCodes
 */
export function isStaleSlotOrQuoteFailure(reasonCodes) {
  const codes = Array.isArray(reasonCodes)
    ? reasonCodes.map((c) => String(c).toUpperCase())
    : String(reasonCodes || '').toUpperCase().split(/[,\s]+/).filter(Boolean)
  return codes.some((c) => (
    c === 'NO_VALID_SLOT'
    || c === 'PRICE_QUOTE_INVALID'
    || c === 'OPERATIONS_CONFIRMATION_REQUIRED'
    || c === 'CREDENTIAL_STALE'
  ))
}

export function validateMatchBooking(payload, requestOptions) {
  return matchRequest('validateMatch', {}, payload, requestOptions || {})
}

export function holdBooking(payload, requestOptions) {
  return matchRequest('hold', {}, payload, requestOptions)
}

export function getHold(holdId) {
  return matchRequest('getHold', {holdId}, {})
}

export function releaseHold(holdId, requestOptions) {
  return matchRequest('releaseHold', {holdId}, {}, requestOptions)
}

export function checkoutHold(holdId, requestOptions) {
  return matchRequest('checkoutHold', {holdId}, {}, requestOptions)
}

export function getBooking(bookingId) {
  return matchRequest('getBooking', {bookingId}, {})
}

export function getActiveBooking(context = {}) {
  return matchRequest('getActiveBooking', context, {})
}

export function paymentReturn(payload) {
  return matchRequest('paymentReturn', {}, payload)
}

export function cancelBooking(bookingId, requestOptions) {
  return matchRequest('cancelBooking', {bookingId}, {}, requestOptions)
}

export function isMockCheckoutForced() {
  return truthyEnv(process.env.VUE_APP_MATCHMAKING_V2_MOCK_CHECKOUT)
}

/** Demo-only mock checkout; live path is default when V2 API is enabled. */
export function shouldUseMockCheckout() {
  return !isMatchmakingV2ApiEnabled() || isMockCheckoutForced()
}

export function isRecommendationNotActiveError(err) {
  if (!err) {
    return false
  }
  if (err.code === 'RECOMMENDATION_NOT_ACTIVE') {
    return true
  }
  if (err.data && err.data.code === 'RECOMMENDATION_NOT_ACTIVE') {
    return true
  }
  return String(err.message || '').indexOf('RECOMMENDATION_NOT_ACTIVE') !== -1
}

/** True when a match-run outcome is a truthful supply gap (not retryable). */
export function isSupplyGapOutcome(status) {
  const code = String(status || '').toUpperCase()
  return code === 'SUPPLY_GAP'
    || code === 'HARD_PREFERENCE_SUPPLY_GAP'
    || code === 'NO_MEMBERS'
}

/** True when the client may re-run match for a transient dependency failure. */
export function isRetryableMatchFailure(status) {
  const code = String(status || '').toUpperCase()
  return code === 'RETRYABLE_DEPENDENCY_FAILURE'
}

/** Map recommendation members → intake card shape. */
export function membersToPeople(members, lang = 'fa', timezone) {
  if (!Array.isArray(members)) {
    return []
  }
  return members.filter((m) => m && typeof m.explanation_rendered_fa === 'string' && m.explanation_rendered_fa.trim()).map((m, index) => {
    const display = m.display || {}
    const publicList = key => {
      const values = lang === 'en' && Array.isArray(display[key + '_en']) ? display[key + '_en'] : display[key]
      return Array.isArray(values) ? values : []
    }
    const name = display.full_name || display.display_name || ('Therapist ' + m.therapist_id)
    const initial = String(name).trim().charAt(0) || 'ت'
    const slot0 = (m.slot_preview && m.slot_preview[0]) || null
    const quote = m.price_quote || {}
    const explanationFa = m.explanation_rendered_fa || ''
    const photoUrl = display.photo_url || null
    const currency = quote.currency || null
    const tier = quote.price_tier ? String(quote.price_tier) : ''
    // Live cards must use recommendation/display data — never static intake.therapist.{id} keys.
    // Older immutable snapshots may contain the exact QA fixture marker.
    // Keep real clinician titles unchanged; fixture provenance is not a public role.
    const title = [display.role_title, display.subtitle, display.title]
      .find(value => typeof value === 'string' && value.trim() && value.trim() !== 'QA10_ANXIETY_20261024') || ''
    const nearestIso = slot0 && slot0.starts_at ? String(slot0.starts_at) : ''
    const nearestFormatted = nearestIso ? formatLiveSlotDateTime(nearestIso, lang, timezone) : null
    return {
      id: String(m.therapist_id),
      memberId: m.recommendation_member_id,
      initial,
      name,
      title,
      tier,
      reason: explanationFa,
      explanation_rendered_fa: explanationFa || null,
      evidence: '',
      bio: display.bio_fa || '',
      professional: {
        specialty: display.major_field || null,
        experience: Object.prototype.hasOwnProperty.call(display, 'experience_years') ? display.experience_years : (Number(display.years_of_experience) >= 0 && Number(display.years_of_experience) <= 100 && display.years_of_experience != null ? Number(display.years_of_experience) : null),
        approaches: publicList('approaches'),
        populations: publicList('populations'),
        languages: publicList('languages'),
      },
      photo_url: photoUrl,
      photoUrl,
      price: Number(quote.amount) || 0,
      currency,
      nearestKey: null,
      nearestLabel: nearestFormatted ? nearestFormatted.combinedLabel : nearestIso,
      recommended: index === 0 || m.display_order === 1,
      slotPreview: m.slot_preview || [],
      priceQuoteId: quote.quote_id || null,
      sandbox: !isMatchmakingV2ApiEnabled(),
    }
  })
}
